import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useHttpClient } from '@bonusly/http-client';

import { PointExpiryForUser } from '../schemas/point-expiry-user';
import { headers } from './meta';

// The v2 http client already prefixes `/api/v2`, so the path omits it.
export const pointExpiryForUserPath = (userId: string) => `/point_expiry/users/${userId}`;

const POINT_EXPIRY_USER_KEY = 'point-expiry:user';

export const pointExpiryForUserQueryKey = (userId?: string) => (userId ? [POINT_EXPIRY_USER_KEY, userId] : [POINT_EXPIRY_USER_KEY]);

// The value depends on the company's live period AND on the viewer's spendable balance, so a
// redemption or an admin schedule change makes it wrong. It is invalidated explicitly at both of
// those points (see invalidatePointExpiryForUsers below), so the cache can be held for a spell
// rather than refetched on every profile mount.
const POINT_EXPIRY_USER_STALE_TIME_MS = 5 * 60 * 1000;

// On-demand fetch for the profile "N points expire on {date}" line. Kept out of the user
// blueprint on purpose (the backend impact math is a per-user ledger summation), so it is
// requested only when a profile is viewed.
export const usePointExpiryForUser = (userId?: string) => {
	const http = useHttpClient();

	return useQuery({
		enabled: !!userId,
		queryKey: pointExpiryForUserQueryKey(userId),
		staleTime: POINT_EXPIRY_USER_STALE_TIME_MS,
		queryFn: async () => {
			const res = await http.get<unknown>(pointExpiryForUserPath(userId as string), { headers });
			return PointExpiryForUser.parse(res.data);
		},
	});
};

/**
 * Drops every cached profile expiry summary. Called after a redemption (the viewer's spendable
 * balance changed, so the expiring count did too) and after an admin creates, edits or cancels a
 * period (the date and count changed for everyone). Not scoped to one user: an admin schedule
 * change invalidates every profile the session has looked at.
 */
export const invalidatePointExpiryForUsers = (queryClient: QueryClient): void => {
	queryClient.invalidateQueries([POINT_EXPIRY_USER_KEY]);
};

export const useInvalidatePointExpiryForUsers = () => {
	const client = useQueryClient();
	return useCallback(() => invalidatePointExpiryForUsers(client), [client]);
};
